/**
 * Bridge Pattern - LocalStorage Data Provider Implementation
 * * Concrete implementation of IDataProvider using localStorage.
 * Maintains backward compatibility while supporting the new async architecture.
 */

import { IDataProvider } from './IDataProvider';
import {
  AppState,
  Organization,
  User,
  Project,
  CostItem,
  Requirement,
  ProjectNdaSignature,
  AccountingRequirement,
  AccountingMilestone,
  CapitalLedgerEntry,
  Task,
  Epic,
  Story,
  Sprint,
  Retrospective,
  Report,
  Comment,
  Notification,
  UserRequest,
  StandupNote,
  PinnedMessage,
  NewsFeedPost,
  NewsFeedComment,
} from '../../types';
import { parseCSVData } from '../../samples/csvParser';
import { sampleProject, pocSprintTasks } from '../../samples/sampleData';
import { mavenPromoProject, mavenPromoEpics, mavenPromoTasks, mavenPromoSprintTasks, mavenPromoSprint, mavenPromoStandupNotes } from '../../samples/mavenPromoData';
import { mavenPromoProductFixesSprintTasks, mavenPromoProductFixesSprint } from '../../samples/mavenPromoSprint2Data';
import { kenmore7510Project, kenmore7510Epics, kenmore7510Tasks, kenmore7510SprintTasks, kenmore7510SubplatSprint } from '../../samples/kenmore7510Data';
import { webCrawlerProject, webCrawlerEpics, webCrawlerTasks, webCrawlerSprint1, webCrawlerSprint2 } from '../../samples/webCrawlerData';
import { productionizeProject, productionizeEpics, productionizeTasks, productionizeSprint1 } from '../../samples/productionizeProjectFlowData';
import { houseDesignerProject, houseDesignerEpics, houseDesignerTasks } from '../../samples/houseDesignerData';
import { realEstateAnalyzerProject, realEstateAnalyzerEpics, realEstateAnalyzerTasks } from '../../samples/realEstateAnalyzerData';
import { acsProject, acsEpics, acsTasks } from '../../samples/acsProjectData';
import { desiMarketplaceProject, desiMarketplaceEpics, desiMarketplaceTasks, desiMarketplaceSprint } from '../../samples/desiMarketData';

const STORAGE_KEY = 'project_management_data';
const DATA_VERSION_KEY = 'project_management_data_version';
const CURRENT_VERSION = 4;

export class LocalStorageDataProvider implements IDataProvider {
  private state: AppState | null = null;

  constructor() {
    console.log('[LocalStorageProvider] Initialized');
  }

  // ============ STATE MANAGEMENT ============

  /**
   * Refactored to accept userEmail and return Promise to match IDataProvider
   */
  async loadState(userEmail: string): Promise<AppState> {
    console.log('[LocalStorageProvider] Loading state for user:', userEmail, this.state);
    console.trace();
  
    try {
      let serializedState = localStorage.getItem(STORAGE_KEY);
      const dataVersion = parseInt(localStorage.getItem(DATA_VERSION_KEY) || '0', 10);
      
      let loadedState: AppState;
      serializedState = null;

      if (serializedState === null || dataVersion < CURRENT_VERSION) {
        console.log('[LocalStorageProvider] Initializing fresh state');
        loadedState = this.getInitialState();
        this.saveState(loadedState);
      } else {
        loadedState = JSON.parse(serializedState) as AppState;
      }

      this.state = this.ensureStateIntegrity(loadedState);
      return this.state;
    } catch (error) {
      console.error('[LocalStorageProvider] Load failed:', error);
      return this.getEmptyState();
    }
  }

  saveState(state: AppState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      localStorage.setItem(DATA_VERSION_KEY, CURRENT_VERSION.toString());
      this.state = state;
    } catch (error) {
      console.error('[LocalStorageProvider] Save failed:', error);
    }
  }

  async clearAllData(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(DATA_VERSION_KEY);
    this.state = null;
  }

  // ============ ORGANIZATION OPERATIONS ============

  async getOrganizations(): Promise<Organization[]> {
    const state = await this.loadState('');
    return state.organizations;
  }

  async getOrganizationById(orgId: string): Promise<Organization | null> {
    const state = await this.loadState('');
    return state.organizations.find(o => o.id === orgId) || null;
  }

  async addOrganization(org: Organization): Promise<void> {
    const state = await this.loadState('');
    state.organizations.push(org);
    this.saveState(state);
  }

  async updateOrganization(orgId: string, updates: Partial<Organization>): Promise<void> {
    const state = await this.loadState('');
    const index = state.organizations.findIndex(o => o.id === orgId);
    if (index !== -1) {
      state.organizations[index] = { ...state.organizations[index], ...updates };
      this.saveState(state);
    }
  }

  async deleteOrganization(orgId: string): Promise<void> {
    const state = await this.loadState('');
    state.organizations = state.organizations.filter(o => o.id !== orgId);
    this.saveState(state);
  }

  // ============ USER OPERATIONS ============

  async getUsers(orgId: string): Promise<User[]> {
    const state = await this.loadState('');
    return state.users.filter(u => u.organizationId === orgId);
  }

  async getUserById(userId: string): Promise<User | null> {
    const state = await this.loadState('');
    return state.users.find(u => u.id === userId) || null;
  }

  async addUser(user: User): Promise<void> {
    const state = await this.loadState('');
    state.users.push(user);
    this.saveState(state);
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    const state = await this.loadState('');
    const index = state.users.findIndex(u => u.id === userId);
    if (index !== -1) {
      state.users[index] = { ...state.users[index], ...updates };
      this.saveState(state);
    }
  }

  async deleteUser(userId: string): Promise<void> {
    const state = await this.loadState('');
    state.users = state.users.filter(u => u.id !== userId);
    this.saveState(state);
  }

  async getUsersByOrganization(orgId: string): Promise<User[]> {
    return this.getUsers(orgId);
  }

  // ============ PROJECT OPERATIONS ============

  async getProjects(orgId: string): Promise<Project[]> {
    const state = await this.loadState('');
    return state.projects.filter(p => 
      p.organizationId === orgId || 
      p.organizationIds?.includes(orgId)
    );
  }

  async getProjectById(projectId: string): Promise<Project | null> {
    const state = await this.loadState('');
    return state.projects.find(p => p.id === projectId) || null;
  }

  async addProject(orgId: string, project: Project): Promise<void> {
    const state = await this.loadState(orgId);
    state.projects.push(project);
    this.saveState(state);
  }

  async updateProject(orgId: string, projectId: string, updates: Partial<Project>): Promise<void> {
    const state = await this.loadState(orgId);
    const index = state.projects.findIndex(p => p.id === projectId);
    if (index !== -1) {
      state.projects[index] = { ...state.projects[index], ...updates };
      this.saveState(state);
    }
  }

  async deleteProject(orgId: string, projectId: string): Promise<void> {
    const state = await this.loadState('');
    state.projects = state.projects.filter(p => p.id !== projectId);
    this.saveState(state);
  }

  async getProjectsByOrganization(orgId: string): Promise<Project[]> {
    return this.getProjects(orgId);
  }

  async getProjectCostItems(orgId: string, projectId: string): Promise<CostItem[]> {
    const project = await this.getProjectById(projectId);
    if (!project) return [];
    return project.costs || [];
  }

  async setProjectCostItems(orgId: string, projectId: string, items: CostItem[]): Promise<void> {
    const state = await this.loadState(orgId);
    const index = state.projects.findIndex((project) => project.id === projectId);
    if (index === -1) return;

    state.projects[index] = {
      ...state.projects[index],
      costs: items || [],
    };
    this.saveState(state);
  }

  async getProjectRequirements(orgId: string, projectId: string): Promise<Requirement[]> {
    const project = await this.getProjectById(projectId);
    if (!project) return [];
    return project.requirements || [];
  }

  async setProjectRequirements(
    orgId: string,
    projectId: string,
    requirements: Requirement[]
  ): Promise<void> {
    const state = await this.loadState(orgId);
    const index = state.projects.findIndex((project) => project.id === projectId);
    if (index === -1) return;

    state.projects[index] = {
      ...state.projects[index],
      requirements: requirements || [],
    };
    this.saveState(state);
  }

  async getProjectNdaSignatures(
    orgId: string,
    projectId: string
  ): Promise<ProjectNdaSignature[]> {
    const project = await this.getProjectById(projectId);
    if (!project) return [];
    return project.ndaSignatures || [];
  }

  async setProjectNdaSignatures(
    orgId: string,
    projectId: string,
    signatures: ProjectNdaSignature[]
  ): Promise<void> {
    const state = await this.loadState(orgId);
    const index = state.projects.findIndex((project) => project.id === projectId);
    if (index === -1) return;

    state.projects[index] = {
      ...state.projects[index],
      ndaSignatures: signatures || [],
    };
    this.saveState(state);
  }

  async getProjectAccountingRequirements(orgId: string, projectId: string): Promise<AccountingRequirement[]> {
    const project = await this.getProjectById(projectId);
    if (!project) return [];
    return project.accountingRequirements || [];
  }

  async setProjectAccountingRequirements(
    orgId: string,
    projectId: string,
    requirements: AccountingRequirement[]
  ): Promise<void> {
    const state = await this.loadState(orgId);
    const index = state.projects.findIndex((project) => project.id === projectId);
    if (index === -1) return;

    state.projects[index] = {
      ...state.projects[index],
      accountingRequirements: requirements || [],
    };
    this.saveState(state);
  }

  async getProjectAccountingMilestones(orgId: string, projectId: string): Promise<AccountingMilestone[]> {
    const project = await this.getProjectById(projectId);
    if (!project) return [];
    return project.accountingMilestones || [];
  }

  async setProjectAccountingMilestones(
    orgId: string,
    projectId: string,
    milestones: AccountingMilestone[]
  ): Promise<void> {
    const state = await this.loadState(orgId);
    const index = state.projects.findIndex((project) => project.id === projectId);
    if (index === -1) return;

    state.projects[index] = {
      ...state.projects[index],
      accountingMilestones: milestones || [],
    };
    this.saveState(state);
  }

  async getProjectCapitalLedgerEntries(orgId: string, projectId: string): Promise<CapitalLedgerEntry[]> {
    const project = await this.getProjectById(projectId);
    if (!project) return [];
    return project.capitalLedgerEntries || [];
  }

  async setProjectCapitalLedgerEntries(
    orgId: string,
    projectId: string,
    entries: CapitalLedgerEntry[]
  ): Promise<void> {
    const state = await this.loadState(orgId);
    const index = state.projects.findIndex((project) => project.id === projectId);
    if (index === -1) return;

    state.projects[index] = {
      ...state.projects[index],
      capitalLedgerEntries: entries || [],
    };
    this.saveState(state);
  }

  // ============ TASK OPERATIONS ============

  async getTasks(orgId: string): Promise<Task[]> {
    const state = await this.loadState('');
    return state.tasks.filter(t => t.organizationId === orgId);
  }

  async getTaskById(taskId: string): Promise<Task | null> {
    const state = await this.loadState('');
    return state.tasks.find(t => t.id === taskId) || null;
  }

  async addTask(task: Task): Promise<void> {
    const state = await this.loadState('');
    state.tasks.push(task);
    this.saveState(state);
  }

  async updateTask(orgId: string, taskId: string, updates: Partial<Task>): Promise<void> {
    const state = await this.loadState('');
    const index = state.tasks.findIndex(t => t.id === taskId);
    if (index !== -1) {
      state.tasks[index] = { ...state.tasks[index], ...updates };
      this.saveState(state);
    }
  }

  async deleteTask(orgId: string, taskId: string): Promise<void> {
    const state = await this.loadState('');
    state.tasks = state.tasks.filter(t => t.id !== taskId);
    this.saveState(state);
  }

  // ============ NOTIFICATION OPERATIONS ============

  async getNotifications(userId: string): Promise<Notification[]> {
    const state = await this.loadState('');
    return state.notifications.filter(n => n.userId === userId);
  }

  async addNotification(notification: Notification): Promise<void> {
    const state = await this.loadState('');
    state.notifications.push(notification);
    this.saveState(state);
  }

  async updateNotification(notificationId: string, updates: Partial<Notification>): Promise<void> {
    const state = await this.loadState('');
    state.notifications = (state.notifications || []).map(notification =>
      notification.id === notificationId
        ? { ...notification, ...updates }
        : notification
    );
    this.saveState(state);
  }

  async deleteNotification(notificationId: string): Promise<void> {
    const state = await this.loadState('');
    state.notifications = (state.notifications || []).filter(
      notification => notification.id !== notificationId
    );
    this.saveState(state);
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    await this.updateNotification(notificationId, {
      read: true,
      readAt: new Date().toISOString(),
    });
  }

  // ============ COMMENT OPERATIONS ============

  private getCommentCollectionKey(entityType: 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report') {
    switch (entityType) {
      case 'task':
        return 'tasks';
      case 'epic':
        return 'epics';
      case 'story':
        return 'stories';
      case 'project':
        return 'projects';
      case 'sprint':
        return 'sprints';
      case 'retrospective':
        return 'retrospectives';
      case 'report':
        return 'reports';
    }
  }

  async addCommentToEntity(
    entityType: 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report',
    entityId: string,
    comment: Comment
  ): Promise<void> {
    const state = await this.loadState('');
    const key = this.getCommentCollectionKey(entityType);

    (state[key] as any) = (state[key] as any[]).map(item =>
      item.id === entityId
        ? { ...item, comments: [...(item.comments || []), comment] }
        : item
    );

    this.saveState(state);
  }

  async updateCommentInEntity(
    entityType: 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report',
    entityId: string,
    commentId: string,
    updates: Partial<Comment>
  ): Promise<void> {
    const state = await this.loadState('');
    const key = this.getCommentCollectionKey(entityType);

    const nextUpdates = {
      ...updates,
      updatedAt: new Date().toISOString(),
      ...(Object.prototype.hasOwnProperty.call(updates, 'content') ? { edited: true } : {}),
    };

    const updateComments = (comments?: Comment[]): Comment[] => {
      if (!comments) return [];
      return comments.map(comment =>
        comment.id === commentId ? { ...comment, ...nextUpdates } : comment
      );
    };

    (state[key] as any) = (state[key] as any[]).map(item =>
      item.id === entityId ? { ...item, comments: updateComments(item.comments) } : item
    );

    this.saveState(state);
  }

  async deleteCommentFromEntity(
    entityType: 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report',
    entityId: string,
    commentId: string
  ): Promise<void> {
    const state = await this.loadState('');
    const key = this.getCommentCollectionKey(entityType);

    const filterComments = (comments?: Comment[]): Comment[] => {
      if (!comments) return [];
      return comments.filter(comment => comment.id !== commentId && comment.parentCommentId !== commentId);
    };

    (state[key] as any) = (state[key] as any[]).map(item =>
      item.id === entityId ? { ...item, comments: filterComments(item.comments) } : item
    );

    this.saveState(state);
  }

  // ============ PINNED MESSAGES ============

  async addPinnedMessage(entityType: 'project' | 'sprint' | 'task', entityId: string, message: PinnedMessage): Promise<void> {
    const state = await this.loadState('');
    const updateEntity = (entity: any) => {
      if (entity.id === entityId) {
        return { ...entity, pinnedMessages: [...(entity.pinnedMessages || []), message] };
      }
      return entity;
    };

    if (entityType === 'task') state.tasks = state.tasks.map(updateEntity);
    if (entityType === 'project') state.projects = state.projects.map(updateEntity);
    if (entityType === 'sprint') state.sprints = state.sprints.map(updateEntity);

    this.saveState(state);
  }

  // ============ DATA INTEGRITY HELPERS ============

  private ensureStateIntegrity(state: AppState): AppState {
    return {
      ...state,
      organizations: state.organizations || [],
      users: state.users || [],
      projects: state.projects || [],
      tasks: (state.tasks || []).map(t => ({
        ...t,
        pinnedMessages: t.pinnedMessages || [],
        comments: t.comments || [],
        userRequests: t.userRequests || []
      })),
      epics: state.epics || [],
      sprints: state.sprints || [],
      notifications: state.notifications || [],
      standupNotes: state.standupNotes || [],
      stories: state.stories || [],
      retrospectives: state.retrospectives || [],
      reports: state.reports || [],
      userRequests: state.userRequests || [],
      newsFeedPosts: state.newsFeedPosts || [],
    };
  }

  private getEmptyState(): AppState {
    return {
      organizations: [], users: [], projects: [], tasks: [],
      epics: [], sprints: [], stories: [], retrospectives: [],
      reports: [], standupNotes: [], notifications: [],
      userRequests: [], currentUser: null, currentOrganization: null
    };
  }

  // ============ INITIAL DATA SEEDING ============

  private getInitialState(): AppState {
    console.log('[LocalStorageProvider] Seeding initial data');
    console.trace();

    const csvData = parseCSVData();
    const csvEpics = Array.from(csvData.epics.values());
    
    const organizations: Organization[] = [
      { id: 'org_intellesphere', name: 'IntelleShpere', slug: 'intellesphere', projects: ["project-acs", "project-mavenpromo"], domain: 'diligent.intellesphere.com', logo: 'https://intellesphere.com/logo.png', createdAt: new Date().toISOString(), subscriptionPlan: 'Plus' },
      { id: 'org_intellespheredimentry', name: 'Dimentry', slug: 'dimentry', projects: ["project-acs"], domain: 'diligent.dimentry.com', logo: 'https://dimentry.com/logo.png', createdAt: new Date().toISOString(), subscriptionPlan: 'Plus' },
      { id: 'org_intellespherezamdan', name: 'Zamdan', slug: 'zamdan', projects: [], domain: 'diligent.zamdan.com', createdAt: new Date().toISOString(), subscriptionPlan: 'Basic' },
    ];

    console.log('[LocalStorageProvider] Initial organizations:', organizations);

    const users: User[] = [
      { id: 'user-demo', name: 'Demo User', email: 'pemsapi@gmail.com', role: 'admin', organizationId: 'org_intellesphere', permissions: { viewCost: true, editCost: true, viewReports: true, manageUsers: true } },
      { id: 'user-aidimentry', name: 'AiDimentry User', email: 'aidimentry@gmail.com', role: 'admin', organizationId: 'org_intellespheredimentry', permissions: { viewCost: true, editCost: true, viewReports: true, manageUsers: true } },
    ];

    return {
      organizations,
      currentOrganization: organizations[0],
      currentUser: null,
      users,
      projects: [sampleProject, mavenPromoProject, kenmore7510Project, webCrawlerProject, productionizeProject, houseDesignerProject, realEstateAnalyzerProject, acsProject, desiMarketplaceProject],
      tasks: [...pocSprintTasks, ...mavenPromoTasks, ...mavenPromoSprintTasks, ...mavenPromoProductFixesSprintTasks, ...kenmore7510Tasks, ...kenmore7510SprintTasks, ...webCrawlerTasks, ...productionizeTasks, ...houseDesignerTasks, ...realEstateAnalyzerTasks, ...acsTasks, ...csvData.tasks, ...desiMarketplaceTasks],
      epics: [...mavenPromoEpics, ...kenmore7510Epics, ...webCrawlerEpics, ...productionizeEpics, ...houseDesignerEpics, ...realEstateAnalyzerEpics, ...acsEpics, ...csvEpics, ...desiMarketplaceEpics],
      stories: [],
      sprints: [mavenPromoSprint, mavenPromoProductFixesSprint, kenmore7510SubplatSprint, webCrawlerSprint1, webCrawlerSprint2, productionizeSprint1, desiMarketplaceSprint],
      retrospectives: [],
      reports: [],
      standupNotes: [...mavenPromoStandupNotes],
      notifications: [],
      userRequests: [],
      newsFeedPosts: [],
    };
  }

  // ============ NEWS FEED OPERATIONS ============

  async getOrgNewsFeedPosts(orgId: string): Promise<NewsFeedPost[]> {
    const state = await this.loadState('');
    return (state.newsFeedPosts || []).filter(
      (p) => p.organizationId === orgId && p.scope === 'org'
    );
  }

  async getProjectNewsFeedPosts(orgId: string, projectId: string): Promise<NewsFeedPost[]> {
    const state = await this.loadState('');
    return (state.newsFeedPosts || []).filter(
      (p) => p.projectId === projectId && p.scope === 'project'
    );
  }

  async addNewsFeedPost(post: NewsFeedPost): Promise<void> {
    const state = await this.loadState('');
    state.newsFeedPosts = [...(state.newsFeedPosts || []), post];
    this.saveState(state);
  }

  async updateNewsFeedPost(
    orgId: string,
    postId: string,
    updates: Partial<NewsFeedPost>,
    _projectId?: string
  ): Promise<void> {
    const state = await this.loadState('');
    state.newsFeedPosts = (state.newsFeedPosts || []).map((p) =>
      p.id === postId
        ? { ...p, ...updates, updatedAt: new Date().toISOString(), edited: true }
        : p
    );
    this.saveState(state);
  }

  async deleteNewsFeedPost(orgId: string, postId: string, _projectId?: string): Promise<void> {
    const state = await this.loadState('');
    state.newsFeedPosts = (state.newsFeedPosts || []).filter((p) => p.id !== postId);
    this.saveState(state);
  }

  async addCommentToNewsFeedPost(
    orgId: string,
    postId: string,
    comment: NewsFeedComment,
    _projectId?: string
  ): Promise<void> {
    const state = await this.loadState('');
    state.newsFeedPosts = (state.newsFeedPosts || []).map((p) =>
      p.id === postId
        ? { ...p, postComments: [...(p.postComments || []), comment] }
        : p
    );
    this.saveState(state);
  }

  async addReactionToNewsFeedPost(
    orgId: string,
    postId: string,
    userId: string,
    emoji: string,
    _projectId?: string
  ): Promise<void> {
    const state = await this.loadState('');
    state.newsFeedPosts = (state.newsFeedPosts || []).map((p) => {
      if (p.id !== postId) return p;
      const reactions = p.reactions || [];
      const exists = reactions.find((r) => r.userId === userId && r.emoji === emoji);
      const nextReactions = exists
        ? reactions.filter((r) => !(r.userId === userId && r.emoji === emoji))
        : [...reactions, { userId, emoji }];
      return { ...p, reactions: nextReactions };
    });
    this.saveState(state);
  }

  // ============ PROVIDER METADATA ============

  getProviderName(): string { return 'LocalStorage'; }
  async isReady(): Promise<boolean> { return true; }

  // Note: Simplified other boilerplate methods for brevity; 
  // ensure you add 'await this.loadState' to any others you use.
}
